/**
 * Surveys (1.7.3, D-1.7.3-1…3): a QR code on a flyer, a few questions, and a count. A survey is an RSVP with
 * questions instead of a seat — the open address (`/s/<slug>`), the QR code, the apartment + name match, the
 * "not matched — review" rows and the export are the event's own (shared/open-rsvp.ts), not a second copy.
 *
 * Pure rules shared by the API and the app: the survey's schema (up to ten questions of four kinds, each in English
 * and Spanish), what an answer may hold, when a survey takes answers, and the count per choice. Nothing here reads
 * or writes a file.
 */
import { z } from 'zod';
import { isDateKey } from './dates';
import { OPEN_SLUG, eventSlug } from './open-rsvp';

export const SURVEY_MAX_QUESTIONS = 10;
export const SURVEY_MAX_CHOICES = 10;
export const SHORT_ANSWER_MAX = 200;

/** Pick one · pick several · short answer (one line) · yes / no. */
export const QUESTION_TYPES = ['one', 'many', 'short', 'yesno'] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

/** Named: name + apartment, matched to a tenant. Anonymous: nothing identifying asked, counts only. */
export const SURVEY_ASKS = ['named', 'anonymous'] as const;
export type SurveyAsks = (typeof SURVEY_ASKS)[number];

export interface Both {
  en: string;
  /** Empty = show the English. */
  es: string;
}
/** The words in the reader's language (the English when no Spanish was typed). */
export const inLang = (b: Both, lang: 'en' | 'es') => (lang === 'es' && b.es.trim() ? b.es : b.en);

export interface SurveyChoice extends Both {
  id: string;
}
export interface SurveyQuestion {
  id: string;
  type: QuestionType;
  text: Both;
  /** Pick one / pick several only. */
  choices: SurveyChoice[];
}

/** What a survey is (kept in `_app/surveys/<slug>.json` with what the office pressed since). */
export interface SurveyDef {
  surveyId: string;
  /** The address: `/s/<slug>` — made once, when the survey is made; a corrected title keeps it. */
  slug: string;
  title: Both;
  /** One line shown to the person under the title. */
  purpose: Both;
  asks: SurveyAsks;
  /** The optional email box (never required; never on an anonymous survey). */
  email: boolean;
  /** First and last day it takes answers (yyyyMMdd, New York); null = no limit on that side. */
  openDayKey: string | null;
  closeDayKey: string | null;
  questions: SurveyQuestion[];
}

/* ------------------------------------------------------------------------------------------------
 * New survey / Edit
 * ---------------------------------------------------------------------------------------------- */

const line = (max: number) => z.string().trim().max(max).transform((s) => s.replace(/\s*[\r\n]+\s*/g, ' '));
const both = (max: number) => z.object({ en: line(max).pipe(z.string().min(1, 'required')), es: line(max).optional().default('') });
const optBoth = (max: number) => z.object({ en: line(max).optional().default(''), es: line(max).optional().default('') }).optional().default({ en: '', es: '' });
const dayKey = z.string().refine(isDateKey, 'date');
const ID = /^[a-z0-9]{1,12}$/;
const id = z.string().regex(ID).optional();

export const SurveyQuestionSchema = z
  .object({
    /** Kept when a survey is edited (the answers are stored by it); a new question has none yet. */
    id,
    type: z.enum(QUESTION_TYPES),
    text: both(300),
    choices: z.array(z.object({ id, en: line(120).pipe(z.string().min(1, 'required')), es: line(120).optional().default('') })).max(SURVEY_MAX_CHOICES).optional().default([]),
  })
  .superRefine((q, ctx) => {
    if ((q.type === 'one' || q.type === 'many') && q.choices.length < 2) ctx.addIssue({ code: 'custom', path: ['choices'], message: 'two-choices' });
  });

export const SurveyInputSchema = z
  .object({
    title: both(120),
    purpose: optBoth(200),
    asks: z.enum(SURVEY_ASKS).default('named'),
    email: z.boolean().default(true),
    openDayKey: dayKey.nullable().optional().default(null),
    closeDayKey: dayKey.nullable().optional().default(null),
    questions: z.array(SurveyQuestionSchema).min(1, 'required').max(SURVEY_MAX_QUESTIONS),
  })
  .superRefine((s, ctx) => {
    if (s.openDayKey && s.closeDayKey && s.closeDayKey < s.openDayKey) ctx.addIssue({ code: 'custom', path: ['closeDayKey'], message: 'before-open' });
  })
  .transform((s) => ({
    ...s,
    // an anonymous survey asks nothing that identifies anyone
    email: s.asks === 'anonymous' ? false : s.email,
    questions: s.questions.map((q) => ({ ...q, choices: q.type === 'one' || q.type === 'many' ? q.choices : [] })),
  }));
export type SurveyInput = z.input<typeof SurveyInputSchema>;
export type SurveyParsed = z.output<typeof SurveyInputSchema>;

/** Gives every question and choice its id: the one it had, or the next free one (`q3`, `c2`). Ids never move. */
export function withIds(questions: SurveyParsed['questions']): SurveyQuestion[] {
  const next = (taken: Set<string>, prefix: string) => {
    let n = 1;
    while (taken.has(`${prefix}${n}`)) n++;
    taken.add(`${prefix}${n}`);
    return `${prefix}${n}`;
  };
  const qTaken = new Set(questions.map((q) => q.id).filter((x): x is string => !!x));
  return questions.map((q) => {
    const cTaken = new Set(q.choices.map((c) => c.id).filter((x): x is string => !!x));
    return { id: q.id ?? next(qTaken, 'q'), type: q.type, text: { en: q.text.en, es: q.text.es }, choices: q.choices.map((c) => ({ id: c.id ?? next(cTaken, 'c'), en: c.en, es: c.es })) };
  });
}

/** True when the questions are the same ones (ids, kinds, choices) — only their wording or order may differ. */
export function sameQuestions(a: readonly SurveyQuestion[], b: readonly SurveyQuestion[]): boolean {
  const sig = (qs: readonly SurveyQuestion[]) =>
    qs
      .map((q) => `${q.id}:${q.type}:${q.choices.map((c) => c.id).sort().join(',')}`)
      .sort()
      .join('|');
  return sig(a) === sig(b);
}

/** `SRV-20261008-A3F9C1` + the title → `bingo-or-movie-night-20261008-a3f9c1` (the event's slug rule). */
export const surveySlug = (title: string, surveyId: string) => eventSlug(title, surveyId);
export const SURVEY_SLUG = OPEN_SLUG;
export const SURVEY_ID = /^SRV-\d{8}-[A-F0-9]{6}$/;

/* ------------------------------------------------------------------------------------------------
 * When a survey takes answers
 * ---------------------------------------------------------------------------------------------- */

export type SurveyState = 'ok' | 'not-yet' | 'closed';

/** Open from its open day to the end of its close day (New York days), unless a person pressed Close. */
export function surveyState(s: Pick<SurveyDef, 'openDayKey' | 'closeDayKey'>, closed: boolean, todayKey: string): SurveyState {
  if (closed) return 'closed';
  if (s.closeDayKey && todayKey > s.closeDayKey) return 'closed';
  if (s.openDayKey && todayKey < s.openDayKey) return 'not-yet';
  return 'ok';
}

/* ------------------------------------------------------------------------------------------------
 * The answer
 * ---------------------------------------------------------------------------------------------- */

/** One question's answer: a choice id (pick one), choice ids (pick several), `yes` / `no`, or the words (short answer). */
export type AnswerValue = string | string[];
export type Answers = Record<string, AnswerValue>;

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const DEVICE_ID = /^[a-f0-9]{16,40}$/;

/** What the page sends. Name, apartment and email are read only on a named survey. */
export const SurveySendSchema = z.object({
  name: z.string().trim().max(120).optional().default(''),
  apartment: z.string().trim().max(20).optional().default(''),
  /** Optional. Something that is not an email address is refused in words, never silently kept. */
  email: z.union([z.literal(''), z.string().trim().toLowerCase().max(254).regex(EMAIL, 'email')]).optional().default(''),
  answers: z.record(z.string().regex(ID), z.union([z.string().max(400), z.array(z.string().max(20)).max(SURVEY_MAX_CHOICES)])).default({}),
  /** An anonymous survey: the device's own number (a cookie the page keeps) — one answer a device. */
  device: z.union([z.literal(''), z.string().regex(DEVICE_ID)]).optional().default(''),
});
export type SurveySendInput = z.input<typeof SurveySendSchema>;
export type SurveySendParsed = z.output<typeof SurveySendSchema>;

/** The fields a survey page can show besides the questions (the no-food test reads this list). */
export const SURVEY_FIELDS = ['name', 'apartment', 'email'] as const;

/**
 * Only what the survey asks: an answer to a question it does not have, a choice it does not offer, or an empty
 * answer is dropped; a short answer is one line of at most 200 characters. An answer to nothing at all is `{}`.
 */
export function cleanAnswers(def: Pick<SurveyDef, 'questions'>, raw: Record<string, unknown>): Answers {
  const out: Answers = {};
  for (const q of def.questions) {
    const v = raw[q.id];
    if (v === undefined || v === null) continue;
    const ids = new Set(q.choices.map((c) => c.id));
    if (q.type === 'one') {
      if (typeof v === 'string' && ids.has(v)) out[q.id] = v;
    } else if (q.type === 'many') {
      const picked = Array.isArray(v) ? q.choices.map((c) => c.id).filter((c) => v.includes(c)) : [];
      if (picked.length) out[q.id] = picked;
    } else if (q.type === 'yesno') {
      if (v === 'yes' || v === 'no') out[q.id] = v;
    } else if (typeof v === 'string') {
      const words = v.replace(/\s+/g, ' ').trim().slice(0, SHORT_ANSWER_MAX);
      if (words) out[q.id] = words;
    }
  }
  return out;
}

/* ------------------------------------------------------------------------------------------------
 * The count
 * ---------------------------------------------------------------------------------------------- */

export const YES_NO: readonly SurveyChoice[] = [
  { id: 'yes', en: 'Yes', es: 'Sí' },
  { id: 'no', en: 'No', es: 'No' },
];

export interface QuestionCount {
  id: string;
  type: QuestionType;
  text: Both;
  /** Answers that answered this question. */
  answered: number;
  /** Pick one, pick several, yes / no: each choice and how many picked it (the survey's own order). */
  choices: { id: string; label: Both; count: number }[];
  /** Short answer: the words, newest first. `who` is "Name · Apt" on a named survey, absent on an anonymous one. */
  texts: { text: string; who?: string }[];
}

export interface CountedAnswer {
  answers: Answers;
  /** "Name · Apt" — never given for an anonymous survey. */
  who?: string;
  at: string;
}

/** The count per choice, question by question. Every answer counts once a question; pick several counts each pick. */
export function tally(def: Pick<SurveyDef, 'questions'>, rows: readonly CountedAnswer[]): QuestionCount[] {
  const newest = [...rows].sort((a, b) => b.at.localeCompare(a.at));
  return def.questions.map((q) => {
    const options = q.type === 'yesno' ? YES_NO : q.choices;
    const counts = new Map(options.map((c) => [c.id, 0]));
    const texts: QuestionCount['texts'] = [];
    let answered = 0;
    for (const r of newest) {
      const v = r.answers[q.id];
      if (v === undefined) continue;
      answered++;
      if (q.type === 'short') {
        if (typeof v === 'string') texts.push(r.who ? { text: v, who: r.who } : { text: v });
      } else for (const c of Array.isArray(v) ? v : [v]) if (counts.has(c)) counts.set(c, counts.get(c)! + 1);
    }
    return { id: q.id, type: q.type, text: q.text, answered, choices: q.type === 'short' ? [] : options.map((c) => ({ id: c.id, label: { en: c.en, es: c.es }, count: counts.get(c.id) ?? 0 })), texts };
  });
}

/** "Bingo night: 23 · Movie night: 11 · Both: 7". */
export const countLine = (q: Pick<QuestionCount, 'choices'>, lang: 'en' | 'es' = 'en') => q.choices.map((c) => `${inLang(c.label, lang)}: ${c.count}`).join(' · ');

/* ------------------------------------------------------------------------------------------------
 * The page (/s/<slug>) — public
 * ---------------------------------------------------------------------------------------------- */

export interface SurveyPublicPage {
  /** `off` = the open address is turned off (a person's own link from the email still works). */
  state: SurveyState | 'off' | 'not-found';
  survey: Pick<SurveyDef, 'title' | 'purpose' | 'asks' | 'email' | 'questions' | 'closeDayKey'> | null;
  /** The person's own link (from the email): their name and apartment, already filled in. */
  known: { name: string; apartment: string } | null;
  officePhone: string | null;
}

export interface SurveySendResult {
  ok: boolean;
  /** The office's words (English); the page says its own beside them. */
  message: string;
  /** `nothing` = no question was answered · `who` = name or apartment missing. */
  code?: 'nothing' | 'who' | 'closed' | 'not-yet' | 'off' | 'not-found';
  /** This Send changed an earlier answer (the same apartment, or the same device). */
  updated?: boolean;
  page?: SurveyPublicPage;
}

/* ------------------------------------------------------------------------------------------------
 * The office: the list, a survey's page, the presses
 * ---------------------------------------------------------------------------------------------- */

export interface SurveySummary {
  surveyId: string;
  slug: string;
  title: Both;
  asks: SurveyAsks;
  state: SurveyState;
  openLinkOn: boolean;
  openDayKey: string | null;
  closeDayKey: string | null;
  questions: number;
  /** Answers counted (ignored ones left out). */
  answers: number;
  /** Rows waiting for the office (not matched, or a typed email that differs). */
  review: number;
  createdAt: string;
  createdBy: string;
}

export interface SurveysResponse {
  surveys: SurveySummary[];
  todayKey: string;
}

/** By which door an answer came. */
export type SurveyVia = 'open' | 'personal' | 'attached';

/** One answer on a named survey (an anonymous survey lists none). */
export interface SurveyAnswerView {
  /** The answer's own key on this survey (what a review press names). */
  key: string;
  tenantId: string | null;
  name: string;
  unit: string;
  /** `matched` a tenant · `review` not matched, waiting · `guest` kept without a tenant. */
  status: 'matched' | 'review' | 'guest';
  via: SurveyVia;
  at: string;
  /** How many times the apartment sent it (2 = it was changed once). */
  sends: number;
  answers: Answers;
}

export interface SurveyReviewRow {
  key: string;
  /** `unmatched`: no tenant in that apartment answers to that name. `email`: a matched tenant typed an email that is not the one on file. */
  kind: 'unmatched' | 'email';
  name: string;
  apartment: string;
  typedEmail: string;
  /** `email` rows: whose record, and the address it has. */
  tenantId: string | null;
  onFileEmail: string;
  at: string;
}

export interface SurveyDetail {
  survey: SurveyDef & { state: SurveyState; openLinkOn: boolean; closedByPress: boolean; createdAt: string; createdBy: string };
  /** The open address in full on the app's own address (what the QR code holds). */
  openUrl: string;
  todayKey: string;
  /** Answers counted. */
  total: number;
  counts: QuestionCount[];
  /** Named surveys: every counted answer, by apartment. Anonymous: empty — nothing identifies anyone. */
  answers: SurveyAnswerView[];
  review: SurveyReviewRow[];
  /** Named surveys: active tenants with no answer yet, with the phone to call. */
  notAnswered: { tenantId: string; name: string; unit: string; phone: string }[];
  /** Named surveys: tenants in the building (answered + not answered). */
  tenants: number;
  /** Emails this survey added to a record that had none. */
  emailsAdded: { tenantId: string; name: string; unit: string; email: string; at: string }[];
  invited: { at: string | null; count: number };
}

/** A tenant's own answers, for the "Surveys" line on their profile. */
export interface PersonSurvey {
  slug: string;
  title: Both;
  at: string;
  /** Each question with the answer in words. */
  lines: { question: Both; answer: Both }[];
}

export const SurveyResolveSchema = z
  .object({
    key: z.string().trim().min(2).max(80),
    /** Not matched: attach · keep (as a guest) · ignore. A different email: use the typed one · keep the one on file. */
    action: z.enum(['attach', 'keep', 'ignore', 'useEmail', 'keepEmail']),
    tenantId: z.string().trim().min(3).max(50).optional(),
  })
  .refine((v) => v.action !== 'attach' || !!v.tenantId, { message: 'required', path: ['tenantId'] });
export type SurveyResolveInput = z.input<typeof SurveyResolveSchema>;

export const SurveyInviteOneSchema = z
  .object({
    tenantId: z.string().trim().min(3).max(50).optional(),
    email: z.union([z.literal(''), z.string().trim().toLowerCase().max(254).regex(EMAIL, 'email')]).optional().default(''),
  })
  .refine((v) => !!v.tenantId || !!v.email, { message: 'required', path: ['email'] });
export type SurveyInviteOneInput = z.input<typeof SurveyInviteOneSchema>;

/** The answer to a question in words, in both languages (the profile line, the export). */
export function answerInWords(q: SurveyQuestion, v: AnswerValue | undefined): Both {
  if (v === undefined) return { en: '', es: '' };
  if (q.type === 'short') return { en: String(v), es: String(v) };
  const options = q.type === 'yesno' ? YES_NO : q.choices;
  const picked = (Array.isArray(v) ? v : [v]).map((x) => options.find((c) => c.id === x)).filter((c): c is SurveyChoice => !!c);
  return { en: picked.map((c) => c.en).join('; '), es: picked.map((c) => c.es || c.en).join('; ') };
}

/** The one button on the SURVEY_INVITE email: it opens the survey (a tenant's own link fills in their name and apartment). */
export const SURVEY_BUTTON = { en: 'Answer the survey', es: 'Responder la encuesta' } as const;
